import { useMemo, useState } from "react";
import { ChevronRight, ArrowUpRight } from "lucide-react";
import { SafeMarkdown } from "./SafeMarkdown";
import { Confidence } from "./Severity";

/**
 * AnswerLayout — calm, owner-friendly executive response.
 *
 * Renders:
 *   • Confidence pill (top-right, subtle)
 *   • Bottom line       (1-2 sentences, calm 17px body — the decisive answer)
 *   • Why it matters    (collapsed when long, expands with one click)
 *   • What I would do next (numbered verbs, max 5)
 *   • Caveat            (single calm line at the bottom)
 *   • View evidence link (opens drawer)
 *
 * Confidence is informative, not alarming — subtle pill, weak visual weight.
 */

const WHY_COLLAPSE_THRESHOLD = 280;   // chars before we collapse by default
const ACTIONS_MAX = 5;

export function AnswerLayout({
    direct,
    why,
    actions = [],
    caveat,
    confidence,
    onOpenEvidence,
    evidenceHint,
}) {
    const whyLong = (why || "").length > WHY_COLLAPSE_THRESHOLD;
    const [whyOpen, setWhyOpen] = useState(!whyLong);
    const clippedActions = useMemo(
        () => (actions || []).slice(0, ACTIONS_MAX),
        [actions],
    );

    return (
        <div
            className="rounded-xl bg-white border border-stone-100 px-7 py-7 shadow-[0_1px_2px_rgba(24,24,27,0.03)] relative"
            data-testid="ai-answer-layout"
        >
            {confidence && (
                <div
                    className="absolute top-5 right-6 opacity-80"
                    data-testid="ai-confidence-pill"
                >
                    <Confidence level={confidence} />
                </div>
            )}

            {direct && (
                <div data-testid="ai-direct" className={confidence ? "pr-32" : ""}>
                    <div className="text-[11px] uppercase tracking-wider text-stone-400 font-medium mb-1.5">
                        Bottom line
                    </div>
                    <SafeMarkdown className="text-[17px] leading-[1.55] text-stone-900 font-normal">
                        {direct}
                    </SafeMarkdown>
                </div>
            )}

            {why && (
                <div className="mt-6" data-testid="ai-why">
                    <button
                        type="button"
                        onClick={() => setWhyOpen((s) => !s)}
                        className="text-[11px] uppercase tracking-wider text-stone-500 font-medium flex items-center gap-1 hover:text-stone-700 transition-colors"
                    >
                        <ChevronRight
                            size={11}
                            strokeWidth={2.5}
                            className={`transition-transform duration-200 ${whyOpen ? "rotate-90" : ""}`}
                        />
                        Why it matters
                    </button>
                    {whyOpen && (
                        <div className="mt-2 pl-3 ds-fade-in">
                            <SafeMarkdown className="text-[14.5px] leading-[1.6] text-stone-600">
                                {why}
                            </SafeMarkdown>
                        </div>
                    )}
                </div>
            )}

            {clippedActions.length > 0 && (
                <div className="mt-6" data-testid="ai-actions">
                    <div className="text-[11px] uppercase tracking-wider text-stone-500 font-medium mb-2.5">
                        What I would do next
                    </div>
                    <ul className="space-y-2 pl-3">
                        {clippedActions.map((a, i) => (
                            <li
                                key={i}
                                className="flex gap-3 text-[14.5px] text-stone-800 leading-[1.55]"
                            >
                                <span className="text-stone-400 font-mono-num text-[12px] mt-1 flex-shrink-0 w-5">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span>{a}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {caveat && (
                <div
                    className="mt-6 pt-4 border-t border-stone-100 text-[12.5px] text-stone-500 leading-relaxed italic"
                    data-testid="ai-caveat"
                >
                    {caveat}
                </div>
            )}

            {onOpenEvidence && (
                <div className={`mt-${caveat ? 3 : 6} ${caveat ? "" : "pt-4 border-t border-stone-100"}`}>
                    <button
                        type="button"
                        onClick={onOpenEvidence}
                        className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-500 hover:text-stone-800 transition-colors"
                        data-testid="ai-open-evidence"
                    >
                        <span>View evidence</span>
                        {evidenceHint && (
                            <span className="text-stone-400">· {evidenceHint}</span>
                        )}
                        <ArrowUpRight size={11} strokeWidth={2} />
                    </button>
                </div>
            )}
        </div>
    );
}
